import { getBearerToken } from "../../shared/api/postCall/bearerToken";
import { logEventToAmplitude } from "../utils/amplitudeAnalytics";

type WorkerMessage =
  | {
      type: "apiCallSuccess";
      payload: any;
      batchKey: string;
    }
  | {
      type: "apiCallError";
      payload: any;
      error: string;
      batchKey: string;
    }
  | {
      type: "queueCheck";
      payload: any;
      batchKey?: string;
    };

type WorkerMessageHandler = (msg: WorkerMessage) => void;

export class ApiWorkerManager {
  private static instance: ApiWorkerManager;
  private worker: Worker | null = null;

  private messageHandlers: Map<string, WorkerMessageHandler> = new Map();
  private isInitialized = false;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private resetTimerHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private static readonly INACTIVITY_EVENTS = [
    "mousedown",
    "keypress",
    "scroll",
    "touchstart",
    "click",
  ] as const;

  private constructor() {
    // Worker is lazily initialized on first enqueue
    this.setupInactivityTimer();
  }

  private setupInactivityTimer() {
    this.resetTimerHandler = () => {
      if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
      this.inactivityTimer = setTimeout(
        () => {
          console.log(
            "[ApiWorkerManager] Terminating worker after 10 minutes of inactivity",
          );
          this.terminate();
        },
        10 * 60 * 1000,
      );
    };

    ApiWorkerManager.INACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, this.resetTimerHandler!, {
        passive: true,
      }),
    );
    this.resetTimerHandler();
  }

  private removeInactivityListeners() {
    if (this.resetTimerHandler) {
      ApiWorkerManager.INACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, this.resetTimerHandler!),
      );
      this.resetTimerHandler = null;
    }
  }

  private setupOnlineOfflineListeners() {
    this.onlineHandler = () => {
      this.worker?.postMessage({ type: "onlineStatusChange", isOnline: true });
      // Push a fresh token: items queued while offline may have outlived the
      // token the worker last held.
      void this.pushAuthToken();
      console.log(
        "[ApiWorkerManager] System is back online. Restarting queue processor...",
      );
    };

    this.offlineHandler = () => {
      this.worker?.postMessage({ type: "onlineStatusChange", isOnline: false });
      console.log(
        "[ApiWorkerManager] System is offline. Queue items will be preserved for retry.",
      );
    };

    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
  }

  private removeOnlineOfflineListeners() {
    if (this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener("offline", this.offlineHandler);
      this.offlineHandler = null;
    }
  }

  public static getInstance() {
    if (!ApiWorkerManager.instance) {
      ApiWorkerManager.instance = new ApiWorkerManager();
    }
    return ApiWorkerManager.instance;
  }

  private async getWorker(): Promise<Worker> {
    if (!this.worker) {
      try {
        this.worker = new Worker(
          new URL("../workers/apiWorker.ts", import.meta.url),
          { type: "module" },
        );

        this.worker.onmessage = async (event) => {
          const msg: WorkerMessage = event.data;
          await this.handleWorkerMessage(msg);
        };

        this.worker.onerror = (err) => {
          console.error("[ApiWorkerManager] Worker error event:", err);
        };

        this.setupOnlineOfflineListeners();
        this.isInitialized = true;
        void this.pushAuthToken();
      } catch (error) {
        console.error("[ApiWorkerManager] Failed to create worker:", error);
        throw error;
      }
    }

    if (!this.worker) {
      throw new Error("[ApiWorkerManager] Worker could not be created");
    }

    return this.worker;
  }

  /**
   * Handle messages from the worker
   */
  private async handleWorkerMessage(msg: WorkerMessage) {
    if (msg.type === "queueCheck") {
      console.log("[ApiWorkerManager] Item added to queue:", msg.payload);
      return;
    }

    // Forward apiCallSuccess and apiCallError to Amplitude regardless of batchKey
    if (msg.type === "apiCallSuccess") {
      const event = msg.payload;
      const cleanedEventNameWithoutBrackets = event?.event_name?.split("(")[0];

      logEventToAmplitude(
        {
          event_name: cleanedEventNameWithoutBrackets || "unknown_event",
          order_id: event?.order_id,
          org_id: event?.org_id,
          user_id: event?.user_id,
          user_email: event?.user_email,
        },
        {
          ...event?.additional_data,
          bigquery_success: true,
          background_processed: true,
          batch_key: msg.batchKey,
        },
      );
    } else if (msg.type === "apiCallError") {
      const event = msg.payload;
      const cleanedEventNameWithoutBrackets = event?.event_name?.split("(")[0];

      logEventToAmplitude(
        {
          event_name: cleanedEventNameWithoutBrackets || "unknown_event",
          order_id: event?.order_id,
          org_id: event?.org_id,
          user_id: event?.user_id,
          user_email: event?.user_email,
        },
        {
          ...event?.additional_data,
          bigquery_success: false,
          bigquery_error: msg.error,
          background_processed: true,
          batch_key: msg.batchKey,
        },
      );
    }

    // Call batch-specific handler if one exists
    if (msg.batchKey) {
      const handler = this.messageHandlers.get(msg.batchKey);
      if (handler) {
        handler(msg);
      }
    }
  }

  /**
   * Forward the current RBAC bearer token to the worker.
   *
   * The worker cannot read the main-thread auth singleton, so it relies on the
   * manager to push the token (on worker creation, each enqueue, and reconnect).
   * Failures are swallowed — the worker simply keeps queueing until a token
   * arrives.
   */
  private async pushAuthToken() {
    if (!this.worker) return;
    try {
      const token = await getBearerToken();
      this.worker?.postMessage({ type: "setAuthToken", token });
    } catch (error) {
      console.error(
        "[ApiWorkerManager] Failed to push auth token to worker:",
        error,
      );
    }
  }

  /**
   * Start the queue processor in the worker
   */
  private startQueueProcessor(delayMs?: number) {
    if (this.worker) {
      this.worker.postMessage({
        type: "startQueueProcessor",
        ...(delayMs !== undefined && { delayMs }),
      });
    }
  }

  /**
   * Add payloads to IndexedDB queue and start processing (fire-and-forget).
   *
   * Returns immediately after queuing. Processing happens asynchronously in a Web Worker
   * with automatic retries. Events are persisted in IndexedDB and will be retried after page reload.
   *
   * @param payloads - Array of payloads or a single payload object to queue.
   * @param batchKey - Optional key to identify this batch for message handlers.
   * @param delayMs - Optional delay (ms) between processing items. Overrides default delay.
   * @param onMessage - Optional callback to handle worker messages for this batch.
   *
   * @throws Never throws. All errors are caught and logged to console.
   *
   * @remarks
   * **Queueing failures:** Errors are logged to console with "[ApiWorkerManager] Failed to enqueue items:"
   * but no exception is thrown. The items will not be queued if worker initialization fails.
   *
   * **Message callbacks:** The `onMessage` callback receives worker messages (apiCallSuccess, apiCallError)
   * for items in this batch. Use this to track processing status for specific batches.
   */
  async enqueueAndProcess(
    payloads: any[] | any,
    batchKey?: string,
    delayMs?: number,
    onMessage?: WorkerMessageHandler,
  ) {
    try {
      // Reset inactivity timer on activity
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = setTimeout(
          () => this.terminate(),
          10 * 60 * 1000,
        );
      }

      const worker = await this.getWorker();

      // Refresh the worker's token before each batch so it stays valid as the
      // RBAC token rotates (the worker can't fetch it itself).
      await this.pushAuthToken();

      // Normalize to array: if single object, wrap it in an array
      const items = Array.isArray(payloads) ? payloads : [payloads];

      worker.postMessage({
        type: "enqueueItems",
        items,
        batchKey,
        ...(delayMs !== undefined && { delayMs }),
      });

      if (onMessage) {
        this.messageHandlers.set(batchKey || "default", onMessage);
      }
    } catch (error) {
      console.error("[ApiWorkerManager] Failed to enqueue items:", error);
    }
  }

  /**
   * Remove handler for specific batchKey
   */
  clearBatchHandler(batchKey: string) {
    this.messageHandlers.delete(batchKey);
  }

  /**
   * Terminate worker and stop processing
   */
  terminate() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    this.removeInactivityListeners();
    this.removeOnlineOfflineListeners();

    if (this.worker) {
      this.worker.postMessage({ type: "stopQueueProcessor" });
      this.worker.terminate();
      this.worker = null;
    }

    this.messageHandlers.clear();
    this.isInitialized = false;
  }
}

export const apiWorkerManager = ApiWorkerManager.getInstance();
